// includes/js-src/janus-engine/pl/passes.ts — the flat calculators of calc_pl (calc_pl_system.php), ported one for one:
//   1 calculate_essences_and_senses  2 calculate_speeds  3 calculate_special_checks  4 calculate_status_effect_modifiers
//   5 calculate_skill_tiers  6 calculate_skill_roll_modifiers  7 calculate_roll_type_damage_ap_force_light_misc
//   9 calculate_armor_modifiers  10 calculate_spells_and_vitality  11 calculate_inventory_costs  12 calculate_status_application_and_triggers
// (8 calculate_abilities lives in abilities.ts with the pricer.) Each takes the shared accumulator `acc` {breakdown, totalGp}
// and mutates it exactly where PHP mutates `$breakdown` / `$total_gp`. Plan §6.5: every cast and round() is PHP's.
import type { ItemFieldRow } from '../compile/item-field-reader';
import { readItemNumericSlot } from '../compile/enchantment-template';
import { readLayerListEntryUuid } from '../compile/inventory-raw-readers';
import { phpJsonTruthy, phpJsonInt } from '../compile/php-json-values';
import { isPhpNumeric, phpJsonFloat } from '../compile/php-decimal-values';
import { phpRound, phpInt, phpIntRound } from './php-round';
import { PlCost, SpellCost, SkillTier, sizeGatedDefenseGp } from './cost';
import { plCatalogs, plCostContract, checkUuid, type PlCostContract } from './contract';
import { plCostLayerEntryUuid, abilityEntryId, abilityEntryName } from './layer-cells';
import type { PlReadContext, Row } from './read-context';
import { isObj } from './read-context';
import { entriesList, heldInventoryEntries } from './economy';
import { requireDefinition } from './xref';

export type Breakdown = Record<string, Row>;
export interface PlAccumulator { breakdown: Breakdown; totalGp: number; sep: Record<string, number> | null }
export interface PassEnv { ctx: PlReadContext; costs: PlCost; spells: SpellCost; tiers: SkillTier; contract: PlCostContract }

export const PL_BREAKDOWN_BUCKETS = ['essences', 'senses', 'speed_types', 'speed_change_remaining', 'special', 'status_effect_modifiers', 'skill_tiers', 'skill_roll_modifiers',
    'roll_type_modifiers', 'damage_modifiers', 'light_modifiers', 'misc_modifiers', 'ability_modifiers', 'vitality', 'inventory', 'ap_modifiers', 'duration_modifiers',
    'at_will_spells', 'innate_spells', 'prepared_spells', 'armor_modifiers'] as const;
/** Dynamic-key dictionaries (uuid / variable-name keys off the wire) are null-prototype, so a hostile `__proto__` key is data, not a setter. */
export const dict = (): Row => Object.create(null) as Row;
export function newAccumulator(): PlAccumulator {
    const breakdown: Breakdown = dict() as Breakdown;
    for (const b of PL_BREAKDOWN_BUCKETS) breakdown[b] = dict();
    return { breakdown, totalGp: 0.0, sep: null };
}
export function makeEnv(ctx: PlReadContext, contract: PlCostContract = plCostContract): PassEnv {
    return { ctx, costs: new PlCost(contract), spells: new SpellCost(contract), tiers: new SkillTier(contract), contract };
}
const nz = (v: unknown): unknown => v === undefined ? null : v;
export const lv = (env: PassEnv, row: Row, name: string): unknown => nz(env.ctx.layerValue(row as ItemFieldRow, name));
export const le = (env: PassEnv, row: Row, name: string): unknown[] => entriesList(env.ctx.layerEntries(row as ItemFieldRow, name));
export const lh = (env: PassEnv, row: Row, name: string): boolean => env.ctx.hasLayer(row as ItemFieldRow, name);
export const itemField = (env: PassEnv, row: Row, key: string): unknown => nz(env.ctx.itemField(row as ItemFieldRow, key));
export const itemType = (env: PassEnv, row: Row): string => { const t = itemField(env, row, 'item_type'); return typeof t === 'string' ? t : ''; };
const bucket = (acc: PlAccumulator, name: string): Row => (acc.breakdown[name] ??= dict());
const sub = (parent: Row, key: string): Row => {
    const own = Object.prototype.hasOwnProperty.call(parent, key) ? parent[key] : undefined;
    if (isObj(own)) return own;
    const fresh = dict(); parent[key] = fresh; return fresh;
};
/** (int) round($gp / 100) */
const plOf = (gp: number): number => phpIntRound(gp / 100);

/** ft_pl_is_content_priced() */
export function isContentPriced(env: PassEnv, row: Row): boolean {
    const t = itemType(env, row);
    return t === 'spell' || env.contract.abilityTypes.includes(t);
}

// ---------------------------------------------------------------------------------------------------------------------
// HELPER 1: ESSENCES & SENSES
export function calculateEssencesAndSenses(env: PassEnv, itemArr: Row, acc: PlAccumulator, isCreature: boolean, forceContent = false): void {
    if (!forceContent && isContentPriced(env, itemArr)) return;
    const cats = plCatalogs(env.contract);
    const categories: Array<[string, readonly { variable_name: string }[]]> = [['essences', cats.essences.rows], ['senses', cats.senses.rows]];
    for (const [catName, list] of categories) {
        const divisor = env.costs.getDivisor(catName);
        for (const entry of list) {
            const varName = entry.variable_name ?? '';
            if (!varName) continue;
            const rawValue = phpJsonInt(lv(env, itemArr, varName) ?? 0);
            if (rawValue === 0) continue;
            if (isCreature && varName === 'fortune' && rawValue === 2) continue;
            const gpPer = isCreature ? env.costs.getCreatureCost(catName, varName, itemArr as ItemFieldRow, (r, n) => env.ctx.hasLayer(r, n)) : env.costs.getCost(catName, varName, 'stackable');
            const units = rawValue / divisor;
            let gp = units * gpPer;
            const entryBd: Row = { raw_value: rawValue, units, gp_per_unit: gpPer };
            if (isCreature && gp < 0) { gp *= 0.25; entryBd.flaw_discount = 0.25; }
            if (isCreature && catName === 'senses' && gp > 0 && units > 12) { gp = (12 * gpPer) + ((units - 12) * gpPer * 0.25); entryBd.range_taper = '60ft full, 25% beyond'; }
            entryBd.gp = gp;
            entryBd.pl = plOf(gp);
            bucket(acc, catName)[varName] = entryBd;
            acc.totalGp += gp;
        }
    }
    if (!isCreature) {
        for (const entry of cats.essences.rows) {
            const base = entry.variable_name ?? '';
            if (base === '') continue;
            for (const prefix of ['ancestral_', 'background_', 'feat_', 'ability_', 'inventory_']) {
                const lname = prefix + base;
                const rawValue = phpJsonInt(lv(env, itemArr, lname) ?? 0);
                if (rawValue === 0) continue;
                const gpPer = env.costs.getCost('essences', base, 'fixed');
                const gp = rawValue * gpPer;
                bucket(acc, 'essences')[lname] = { raw_value: rawValue, units: rawValue, gp_per_unit: gpPer, gp, pl: plOf(gp) };
                acc.totalGp += gp;
            }
        }
    }
}

// HELPER 2: SPEEDS
export function calculateSpeeds(env: PassEnv, itemArr: Row, acc: PlAccumulator, isCreature: boolean, forceContent = false): void {
    if (!forceContent && isContentPriced(env, itemArr)) return;
    for (const entry of plCatalogs(env.contract).speedTypes.rows) {
        const varName = entry.variable_name ?? '';
        if (!varName || !(varName.startsWith('speed_') || varName === 'teleport')) continue;
        const rawValue = phpJsonInt(lv(env, itemArr, varName) ?? 0);
        if (rawValue === 0) continue;
        const units = rawValue / 5;
        const gpPer = isCreature ? env.costs.getCreatureCost('speed_types', varName, itemArr as ItemFieldRow, (r, n) => env.ctx.hasLayer(r, n)) : env.costs.getCost('speed_types', varName);
        const gp = units * gpPer;
        bucket(acc, 'speed_types')[varName] = { raw_value: rawValue, units, gp_per_unit: gpPer, gp, pl: plOf(gp) };
        acc.totalGp += gp;
    }
    for (const varName of ['change_speed_walking_remaining', 'change_speed_jumping_remaining', 'change_speed_climbing_remaining', 'change_speed_flying_remaining', 'change_speed_hovering_remaining', 'change_speed_swimming_remaining', 'change_speed_burrowing_remaining', 'change_teleport_remaining']) {
        const rawValue = phpJsonInt(lv(env, itemArr, varName) ?? 0);
        if (rawValue === 0) continue;
        const units = rawValue / 5;
        const gpPer = env.costs.getCost('speed_change_remaining', varName);
        const gp = Math.min(units * gpPer, 25000);
        bucket(acc, 'speed_change_remaining')[varName] = { raw_value: rawValue, units, gp_per_unit: gpPer, gp, pl: plOf(gp) };
        acc.totalGp += gp;
    }
}

// HELPER 3: SPECIAL CHECKS
function lockpickTierBonus(env: PassEnv, arr: Row): number {
    const bonus = lv(env, arr, 'lockpick_effective_skill_tier_bonus');
    if (bonus === null || bonus === '' || typeof bonus === 'object' || !isPhpNumeric(bonus)) return 0;
    return Math.max(0, Math.min(6, phpJsonInt(bonus)));
}
function lockpickPct(env: PassEnv, arr: Row, layer: string): number {
    const pct = lv(env, arr, layer);
    if (pct === null || pct === '' || typeof pct === 'object' || !isPhpNumeric(pct)) return 0.0;
    const f = phpJsonFloat(pct);
    if (!Number.isFinite(f)) return 0.0;
    return Math.max(env.contract.constants.lockpickPctMin, Math.min(env.contract.constants.lockpickPctMax, f));
}
export function calculateSpecialChecks(env: PassEnv, itemArr: Row, acc: PlAccumulator): void {
    const speedRows = Object.values(acc.breakdown.speed_types ?? {}) as Row[];
    const speedGp = speedRows.length ? speedRows.reduce((s, r) => s + (typeof r.gp === 'number' ? r.gp : 0), 0) : 0;
    const special = bucket(acc, 'special');
    if (phpJsonTruthy(lv(env, itemArr, 'effect_requires_preferred_speed_type'))) {
        const adjusted = speedGp * 0.75;
        const discount = adjusted - speedGp;
        special.effect_requires_preferred_speed_type = { gp: discount, pl: plOf(discount) };
        acc.totalGp += discount;
    }
    if (lv(env, itemArr, 'caster_moves_towards_target_in_ft') !== null) {
        const ft = phpJsonInt(lv(env, itemArr, 'caster_moves_towards_target_in_ft'));
        const gp = (ft / 5) * env.costs.getCost('special', 'caster_moves_towards_target_in_ft');
        special.caster_moves_towards_target_in_ft = { ft, gp, pl: plOf(gp) };
        acc.totalGp += gp;
    }
    for (const flag of ['caster_moves_to_end_point_of_aoe_effect_after_cast', 'caster_dive_bombs_target']) {
        if (!phpJsonTruthy(lv(env, itemArr, flag))) continue;
        const gp = env.costs.getCost('special', flag);
        special[flag] = { gp, pl: plOf(gp) };
        acc.totalGp += gp;
    }
    const grades = lockpickTierBonus(env, itemArr);
    if (grades > 0) {
        const gp = grades * env.costs.getCost('special', 'lockpick_effective_skill_tier_bonus');
        special.lockpick_effective_skill_tier_bonus = { grades, gp, pl: plOf(gp) };
        acc.totalGp += gp;
    }
    for (const pctLayer of ['lockpick_sweet_spot_changed_by_percentage', 'lockpick_pick_durability_changed_by_percentage']) {
        if (lv(env, itemArr, pctLayer) === null) continue;
        const pct = lockpickPct(env, itemArr, pctLayer);
        if (Math.abs(pct) < 0.001) continue;
        const gp = (pct / 100.0) * env.costs.getCost('special', pctLayer);
        special[pctLayer] = { pct, gp, pl: plOf(gp) };
        acc.totalGp += gp;
    }
}

// HELPER 4: STATUS EFFECT MODIFIERS (+ the status_effect_permanent_pl stamp)
export function calculateStatusEffectModifiers(env: PassEnv, itemArr: Row, acc: PlAccumulator, forceContent = false, isCreature = false): void {
    acc.sep = null; // unset($item_arr['computed']['status_effect_permanent_pl'])
    if (!forceContent && isContentPriced(env, itemArr)) return;
    const cats = plCatalogs(env.contract);
    const modifierFields: Array<[string, string]> = [['contesting_roll_adv_vs_status_effect', 'contesting_roll_adv_vs_status_effect'], ['contesting_roll_dis_vs_status_effect', 'contesting_roll_dis_vs_status_effect'], ['immune_status_effect', 'immune_to_status_effect'], ['status_effect_permanent', 'status_effect_permanent']];
    const immuneRows: Array<{ var: string; gp: number }> = [];
    for (const [field, costKey] of modifierFields) {
        const entries = le(env, itemArr, field);
        if (entries.length === 0) continue;
        for (const entry of entries) {
            const seUuid = plCostLayerEntryUuid(entry);
            if (seUuid === '') continue;
            const varName = cats.statusEffects.getVariableName(seUuid);
            if (!varName) continue;
            let gp = env.costs.getStatusEffectModifierCost(costKey, varName);
            if (gp === 0) continue;
            const discount = env.tiers.getRequirementDiscountFromArray(itemArr as ItemFieldRow, (r, n) => env.ctx.layerValue(r, n));
            gp = phpIntRound(gp * discount);
            if (isCreature && field === 'immune_status_effect') { immuneRows.push({ var: varName, gp }); continue; }
            sub(bucket(acc, 'status_effect_modifiers'), field)[varName] = { status: varName, gp, pl: plOf(gp) };
            if (field === 'status_effect_permanent') { (acc.sep ??= dict() as Record<string, number>)[seUuid.toUpperCase()] = phpRound(gp / 100, 2); }
            acc.totalGp += gp;
        }
    }
    if (immuneRows.length) {
        immuneRows.sort((a, b) => b.gp - a.gp); // usort $b <=> $a (stable)
        const stackMults = [1.0, 0.5, 0.25];
        immuneRows.forEach((row, i) => {
            const mult = stackMults[i] ?? 0.1;
            const gp = phpIntRound(row.gp * mult);
            sub(bucket(acc, 'status_effect_modifiers'), 'immune_status_effect')[row.var] = { status: row.var, stack_mult: mult, gp, pl: plOf(gp) };
            acc.totalGp += gp;
        });
    }
}

// HELPER 5: SKILL TIERS
/** caster_skills(): computed.creature.skills (canonical) else the transitional root slot. */
export function casterSkills(arr: Row): Row {
    const computed = isObj(arr.computed) ? arr.computed : null;
    const creature = computed && isObj(computed.creature) ? computed.creature : null;
    if (creature && creature.skills !== undefined && creature.skills !== null) return isObj(creature.skills) ? creature.skills : {};
    if (arr.skills !== undefined && arr.skills !== null) return isObj(arr.skills) ? arr.skills : {};
    return {};
}
/** creature_skill_tier_entries(): the raw tier layer entries ∪ computed.creature.skill_tier_grants[tier] (deduped by uuid). */
function creatureSkillTierEntries(env: PassEnv, itemArr: Row, tierKey: string): unknown[] {
    const entries = le(env, itemArr, tierKey);
    const computed = isObj(itemArr.computed) ? itemArr.computed : null;
    const creature = computed && isObj(computed.creature) ? computed.creature : null;
    const grants = creature && isObj(creature.skill_tier_grants) ? (creature.skill_tier_grants as Row)[tierKey] : null;
    const grantList = entriesList(grants);
    if (grantList.length === 0) return entries;
    const seen = new Set<string>();
    for (const e of entries) { const uid = readLayerListEntryUuid(e); if (uid !== '') seen.add(uid); }
    const out = [...entries];
    for (const g of grantList) { const uid = readLayerListEntryUuid(g); if (uid === '' || seen.has(uid)) continue; seen.add(uid); out.push(g); }
    return out;
}
export function calculateSkillTiers(env: PassEnv, itemArr: Row, acc: PlAccumulator, forceContent = false): void {
    if (!forceContent && isContentPriced(env, itemArr)) return;
    let skillTierGp = 0;
    const compiled = casterSkills(itemArr);
    if (Object.keys(compiled).length > 0) {
        for (const [varName, skill] of Object.entries(compiled)) {
            if (varName === '_meta' || !isObj(skill)) continue;
            const tl = phpJsonInt(skill.tier_level ?? 0);
            const tierVar = typeof skill.tier === 'string' ? skill.tier : ('skill_tier_' + tl); // ft_hydrate_skill_entry adds 'tier' only when absent
            const gp = env.tiers.getGpCost(tierVar);
            if (gp === 0) continue;
            bucket(acc, 'skill_tiers')[varName] = { skill: varName, tier: tierVar, tier_level: skill.tier_level ?? 0, gp, pl: plOf(gp) };
            skillTierGp += gp;
        }
    } else {
        for (const tier of env.contract.skillTiers) {
            const tierKey = tier.variable_name;
            const tierEntries = creatureSkillTierEntries(env, itemArr, tierKey);
            if (tierEntries.length === 0) continue;
            const gpPerSkill = tier.pl_gp_value ?? 0;
            if (gpPerSkill === 0) continue;
            const numSkills = tierEntries.length;
            const gp = gpPerSkill * numSkills;
            bucket(acc, 'skill_tiers')[tierKey] = { tier: tierKey, skills_affected: numSkills, gp_per_skill: gpPerSkill, gp, pl: plOf(gp) };
            skillTierGp += gp;
        }
    }
    acc.totalGp += skillTierGp;
}

// HELPER 6: SKILL ROLL MODIFIERS
export function calculateSkillRollModifiers(env: PassEnv, itemArr: Row, acc: PlAccumulator): void {
    let gpSum = 0;
    const t = itemType(env, itemArr);
    const contentPricedItem = env.contract.abilityTypes.includes(t) || t === 'spell';
    const cats = plCatalogs(env.contract);
    if (!contentPricedItem) {
        for (const key of ['skill_roll_adv', 'skill_roll_dis', 'skill_roll_ignore_dis', 'skill_roll_mod_plus1', 'skill_roll_mod_minus1', 'skill_is_rolled_at_min', 'skill_is_rolled_at_max']) {
            const entries = le(env, itemArr, key);
            if (entries.length === 0) continue;
            for (const entry of entries) {
                const seUuid = plCostLayerEntryUuid(entry);
                if (seUuid === '') continue;
                const varName = cats.skills.getVariableName(seUuid) ?? '';
                if (!varName) continue;
                const gp = env.costs.getSkillRollModifierCost(key, varName);
                if (gp === 0) continue;
                sub(bucket(acc, 'skill_roll_modifiers'), key)[varName] = { skill: varName, gp, pl: plOf(gp) };
                gpSum += gp;
            }
        }
        for (const key of ['all_skill_rolls_adv', 'all_skill_rolls_dis', 'all_skill_rolls_plus1', 'all_skill_rolls_minus1', 'ascension_skill_point_range_mod_minus1']) {
            if (!phpJsonTruthy(lv(env, itemArr, key))) continue;
            const gp = env.costs.getSkillRollModifierCost(key);
            sub(bucket(acc, 'skill_roll_modifiers'), 'global')[key] = { gp, pl: plOf(gp) };
            gpSum += gp;
        }
    }
    acc.totalGp += gpSum;
}

/** ft_pl_misc_per_entry_rows() */
export function miscPerEntryRows(env: PassEnv, arr: Row): Record<string, { value: number; gp: number }> {
    const rows: Record<string, { value: number; gp: number }> = {};
    const perEntry = (env.costs.COSTS.misc_modifiers as Row | undefined)?.per_entry;
    for (const [field, perEntryGp] of Object.entries(isObj(perEntry) ? perEntry : {})) {
        const uuids = new Set<string>();
        for (const e of le(env, arr, field)) { const u = plCostLayerEntryUuid(e).toUpperCase(); if (u !== '') uuids.add(u); }
        if (uuids.size === 0) continue;
        rows[field] = { value: uuids.size, gp: uuids.size * phpInt(Number(perEntryGp)) };
    }
    return rows;
}
/** The misc-field list both pricers iterate, and the per-field special cases (shared by pass 7 and the ability pricer). */
export function miscFieldList(env: PassEnv): string[] {
    return [...env.costs.miscFieldKeys('per_one'), ...env.costs.miscFieldKeys('per_five'), ...env.costs.miscFieldKeys('flat'), 'critical_hit_damage_multiplier', 'ignore_long_range_attack_roll_dis', 'opportunity_attack_roll_adv', 'overrun_roll_adv', 'speed_jumping_multiplier'];
}
export function miscFieldGp(env: PassEnv, field: string, value: number): number {
    switch (field) {
        case 'critical_hit_damage_multiplier': return Math.max(0, value - 2) * 3 * env.costs.getMiscModifierCost('critical_hit_range_increase_mod', 1);
        case 'ignore_long_range_attack_roll_dis': return env.costs.getMiscModifierCost('ignore_ranged_weapon_attack_roll_dis_within_an_enemy_reach', 1);
        case 'opportunity_attack_roll_adv': return phpIntRound(env.costs.getMiscModifierCost('pack_tactics', 1) / 2);
        // Hold the Front: ADV on one niche contest (both sides of an overrun) — priced like its OA twin.
        case 'overrun_roll_adv': return phpIntRound(env.costs.getMiscModifierCost('pack_tactics', 1) / 2);
        case 'speed_jumping_multiplier': return Math.max(0, value - 1) * 2 * env.costs.getCost('speed_types', 'speed_jumping');
        // Line Holder: x2 is the stock occupied-space cost, so only whole steps ABOVE it sell.
        case 'enemy_move_past_movement_cost_multiplier': return Math.max(0, value - 2) * env.costs.getMiscModifierCost('enemy_move_past_movement_cost_multiplier', 1);
        // Heaven's Verdict: LOWER threshold = more spell levels warded, so count down from 10.
        case 'contest_required_to_target_above_spell_level': return Math.max(0, 10 - value) * env.costs.getMiscModifierCost('contest_required_to_target_above_spell_level', 1);
        // Psionic Siegecraft / Siege Throw: x1 is the stock damage, so only whole steps ABOVE it sell.
        case 'damage_to_objects_multiplier': return Math.max(0, value - 1) * env.costs.getMiscModifierCost('damage_to_objects_multiplier', 1);
        default: return env.costs.getMiscModifierCost(field, value);
    }
}

// HELPER 7: ROLL TYPE / DAMAGE / AP / FORCE / LIGHT / MISC
export function calculateRollTypeDamageApForceLightMisc(env: PassEnv, itemArr: Row, acc: PlAccumulator, isCreature: boolean): void {
    const t = itemType(env, itemArr);
    const isAbilityItem = env.contract.abilityTypes.includes(t);
    const contentPricedItem = isAbilityItem || t === 'spell';
    const cats = plCatalogs(env.contract);
    let rollTypeModGp = 0;
    if (!contentPricedItem) {
        for (const field of ['roll_type_adv', 'roll_type_dis', 'roll_type_against_adv', 'roll_type_against_dis', 'roll_type_is_rolled_at_min', 'roll_type_is_rolled_at_max', 'roll_type_roll_result_is_halved', 'roll_type_roll_result_is_doubled']) {
            const entries = le(env, itemArr, field);
            if (entries.length === 0) continue;
            for (const entry of entries) {
                const uuid = plCostLayerEntryUuid(entry);
                if (uuid === '') continue;
                const varName = cats.rollTypes.getVariableName(uuid);
                if (!varName) continue;
                const gp = env.costs.getRollTypeModifierCost(field, varName);
                if (gp === 0) continue;
                sub(bucket(acc, 'roll_type_modifiers'), field)[varName] = { roll_type: varName, gp, pl: plOf(gp) };
                rollTypeModGp += gp;
            }
        }
    }
    acc.totalGp += rollTypeModGp;

    let damageModGp = 0;
    if (!contentPricedItem) {
        const dmgImmuneRows: Array<{ var: string; gp: number }> = [];
        for (const field of ['damage_type_resistance', 'damage_type_immunity', 'damage_type_vulnerability', 'ignore_damage_type_resistance']) {
            const entries = le(env, itemArr, field);
            if (entries.length === 0) continue;
            for (const entry of entries) {
                const uuid = plCostLayerEntryUuid(entry);
                if (uuid === '') continue;
                const varName = cats.damageTypes.getVariableName(uuid);
                if (!varName) continue;
                let gp = env.costs.getDamageModifierCost(field, varName);
                if (gp === 0) continue;
                const entryBd: Row = { damage_type: varName };
                if (isCreature && field === 'damage_type_immunity') { dmgImmuneRows.push({ var: varName, gp }); continue; }
                if (isCreature && field === 'damage_type_vulnerability' && gp < 0) { gp = phpIntRound(gp * 0.25); entryBd.flaw_discount = 0.25; }
                entryBd.gp = gp; entryBd.pl = plOf(gp);
                sub(bucket(acc, 'damage_modifiers'), field)[varName] = entryBd;
                damageModGp += gp;
            }
        }
        if (dmgImmuneRows.length) {
            dmgImmuneRows.sort((a, b) => b.gp - a.gp);
            const stackMults = [1.0, 0.5, 0.25];
            dmgImmuneRows.forEach((row, i) => {
                const mult = stackMults[i] ?? 0.1;
                const gp = phpIntRound(row.gp * mult);
                sub(bucket(acc, 'damage_modifiers'), 'damage_type_immunity')[row.var] = { damage_type: row.var, stack_mult: mult, gp, pl: plOf(gp) };
                damageModGp += gp;
            });
        }
        for (const key of ['spell_damage_resistance', 'spell_damage_vulnerability', 'immune_to_spells', 'immune_to_hostile_spells', 'force_move_resistance', 'immune_to_forced_movement', 'immune_to_disarm', 'ignores_difficult_terrain', 'ignores_snow_and_ice_terrain', 'walks_on_water_and_ice', 'burrows_worked_stone', 'ignores_occupied_space_movement_penalty']) {
            if (!phpJsonTruthy(lv(env, itemArr, key))) continue;
            const gp = env.costs.getDamageModifierCost(key);
            sub(bucket(acc, 'damage_modifiers'), 'global')[key] = { gp, pl: plOf(gp) };
            damageModGp += gp;
        }
        for (const key of env.contract.constants.sizeGatedDefenseLayers) {
            const value = lv(env, itemArr, key);
            if (!isPhpNumeric(value)) continue;
            const gp = sizeGatedDefenseGp(key, phpJsonInt(value), env.costs);
            sub(bucket(acc, 'damage_modifiers'), 'global')[key] = { value: phpJsonInt(value), gp, pl: plOf(gp) };
            damageModGp += gp;
        }
        for (const [key, label] of [['all_damage_output_changed_by_percentage', 'value'], ['all_incoming_damage_changed_by_percentage', 'value'], ['negate_falling_damage_in_feet', 'feet']] as const) {
            if (!phpJsonTruthy(lv(env, itemArr, key))) continue;
            const value = phpJsonInt(lv(env, itemArr, key));
            const gp = env.costs.getDamageModifierCost(key, '', value);
            sub(bucket(acc, 'damage_modifiers'), 'global')[key] = { [label]: value, gp, pl: plOf(gp) };
            damageModGp += gp;
        }
    }
    acc.totalGp += damageModGp;

    if (!contentPricedItem) {
        let apModGp = 0;
        for (const field of ['ap_change_at_start_of_turn', 'ap_change_during_turn', 'weapon_attack_count_change', 'ap_change_to_caster_during_turn', 'reaction_reset']) {
            const value = phpJsonInt(lv(env, itemArr, field) ?? 0);
            if (value === 0) continue;
            const gp = value * env.costs.getApModifierCost(field);
            bucket(acc, 'ap_modifiers')[field] = { value, gp, pl: plOf(gp) };
            apModGp += gp;
        }
        acc.totalGp += apModGp;

        let lightGp = 0;
        let multiplier = 1.0;
        if (phpJsonTruthy(lv(env, itemArr, 'magical_light'))) multiplier *= env.costs.getLightModifierCost('magical_light');
        if (phpJsonTruthy(lv(env, itemArr, 'magical_darkness'))) multiplier *= env.costs.getLightModifierCost('magical_darkness');
        for (const field of ['bright_light_dim_light', 'limited_visibility_ft']) {
            const ft = phpJsonInt(lv(env, itemArr, field) ?? 0);
            if (ft === 0) continue;
            const baseGp = env.costs.getLightModifierCost(field, ft);
            const gp = phpIntRound(baseGp * multiplier);
            bucket(acc, 'light_modifiers')[field] = { ft, base_gp: baseGp, multiplier, gp, pl: plOf(gp) };
            lightGp += gp;
        }
        acc.totalGp += lightGp;

        let miscGp = 0;
        const armorOwned = ['ac_mod', 'ac_mod_magical', 'ac_mod_non_magical', 'damage_mitigation'];
        for (const field of miscFieldList(env)) {
            if (armorOwned.includes(field)) continue;
            let value = phpJsonInt(lv(env, itemArr, field) ?? 0);
            if (value === 0) continue;
            if (field === 'can_breath_underwater') value = 1;
            if (isCreature && field === 'arcane_threshold' && value === 1) continue;
            const gp = miscFieldGp(env, field, value);
            bucket(acc, 'misc_modifiers')[field] = { value, gp, pl: plOf(gp) };
            miscGp += gp;
        }
        for (const [field, row] of Object.entries(miscPerEntryRows(env, itemArr))) {
            bucket(acc, 'misc_modifiers')[field] = { ...row, pl: plOf(row.gp) };
            miscGp += row.gp;
        }
        acc.totalGp += miscGp;
    }

    if (!contentPricedItem) {
        let forceGp = 0;
        for (const field of ['pull_ft_after_hit', 'push_ft_after_hit']) {
            const ft = phpJsonInt(lv(env, itemArr, field) ?? 0);
            if (ft === 0) continue;
            const gp = env.costs.getForceMovementModifierCost(field, ft);
            bucket(acc, 'force_movement_modifiers')[field] = { ft, gp, pl: plOf(gp) };
            forceGp += gp;
        }
        for (const field of ['push_any_direction', 'pull_to_caster_center']) {
            if (!phpJsonTruthy(lv(env, itemArr, field))) continue;
            const gp = env.costs.getForceMovementModifierCost(field);
            bucket(acc, 'force_movement_modifiers')[field] = { gp, pl: plOf(gp) };
            forceGp += gp;
        }
        acc.totalGp += forceGp;
    }
}

// HELPER 9: ARMOR MODIFIERS
export function calculateArmorModifiers(env: PassEnv, itemArr: Row, acc: PlAccumulator): void {
    let armorGp = 0.0;
    const t = itemType(env, itemArr);
    if (env.contract.abilityTypes.includes(t) || t === 'spell') return;
    const rowFor = (field: string, gpFn: (v: number) => number): void => {
        if (!phpJsonTruthy(lv(env, itemArr, field))) return;
        const value = phpJsonInt(lv(env, itemArr, field));
        if (value === 0) return;
        const gp = gpFn(value);
        bucket(acc, 'armor_modifiers')[field] = { value, gp, pl: phpRound(gp / 100, 2) };
        armorGp += gp;
    };
    rowFor('ac_mod', v => env.costs.getBaseAcModCost(v));
    rowFor('damage_mitigation', v => env.costs.getDamageMitigationCost(v));
    rowFor('ac_mod_magical', v => env.costs.getMiscModifierCost('ac_mod_magical', v));
    rowFor('ac_mod_non_magical', v => env.costs.getMiscModifierCost('ac_mod_non_magical', v));
    acc.totalGp += armorGp;
}

// HELPER 10: SPELLS + VITALITY
import { layerEntryNeedsResolve, resolveLayerEntry } from '../compile/layer-entry-resolver';
import { calculateAbilityEconomyAndRestrictions } from './economy';
/** ft_entry_restore_item_arr(): a bare `item_id` uuid ref without an item_arr key gets its item_arr slot back. */
export function entryRestoreItemArr(entry: Row): Row {
    if (!Object.prototype.hasOwnProperty.call(entry, 'item_arr') && typeof entry.item_id === 'string' && checkUuid(entry.item_id)) return { ...entry, item_arr: entry.item_id };
    return entry;
}
/** layer_has($entry, 'item_arr') on an ENTRY: the legacy top-level slot read finds a present non-null item_arr. */
export const entryHasItemArr = (entry: Row): boolean => entry.item_arr !== undefined && entry.item_arr !== null && entry.item_arr !== '' && !(Array.isArray(entry.item_arr) && entry.item_arr.length === 0);
/** The entry's ability_arr the way calc_pl resolves it: the item_arr slot (resolved when a bare ref / shell) — or null when the entry has none. */
export function resolveEntryItemArr(env: PassEnv, entry: Row, root: Row): Row | null {
    let arr: unknown = entry.item_arr;
    if (layerEntryNeedsResolve(arr)) arr = resolveLayerEntry(arr, root as ItemFieldRow, id => requireDefinition(env.ctx, id) as ItemFieldRow);
    return isObj(arr) ? arr : (Array.isArray(arr) ? (arr as unknown as Row) : null);
}
/** `$entry[0] ?? ''` for a string-or-array entry. */
const entryZero = (entry: unknown): string => typeof entry === 'string' ? entry : (Array.isArray(entry) ? (typeof entry[0] === 'string' ? entry[0] : '') : (isObj(entry) && typeof entry['0'] === 'string' ? entry['0'] : ''));
export function calculateSpellsAndVitality(env: PassEnv, itemArr: Row, acc: PlAccumulator): void {
    let spellGp = 0;
    const spellLayerTypes: Array<[string, string]> = [['at_will_spell', 'at_will_spell'], ['innate_spell_per_long_rest', 'innate_spell'], ['charged_spell', 'charged_spell'], ['charged_ability', 'charged_ability']];
    for (const [layerKey, economyType] of spellLayerTypes) {
        const entries = le(env, itemArr, layerKey);
        if (entries.length === 0) continue;
        for (let entry of entries) {
            if (isObj(entry)) entry = entryRestoreItemArr(entry);
            let spellArr: Row | null; let spellId: string; let spellName: string;
            if (isObj(entry) && entryHasItemArr(entry)) {
                spellArr = resolveEntryItemArr(env, entry, itemArr);
                spellId = abilityEntryId(entry, env.ctx.entryDeps);
                spellName = abilityEntryName(entry, env.ctx.entryDeps);
            } else {
                spellId = entryZero(entry);
                if (!spellId) continue;
                spellArr = requireDefinition(env.ctx, spellId);
                const n = itemField(env, spellArr, 'item_name');
                spellName = typeof n === 'string' ? n : 'Unknown Spell';
            }
            if (!spellArr || Object.keys(spellArr).length === 0) continue;
            const level = phpJsonInt(lv(env, spellArr, 'spell_level') ?? lv(env, spellArr, 'ability_activation_charges') ?? lv(env, spellArr, 'level') ?? 0);
            if (level <= 0) continue;
            const baseGp = env.spells.getCostForLevel(level);
            const econ = calculateAbilityEconomyAndRestrictions(env.ctx, spellArr, economyType, itemArr, env.contract);
            const finalGp = phpIntRound(baseGp * econ.multiplier);
            sub(bucket(acc, 'ability_modifiers'), layerKey)[spellId] = { spell_name: spellName + ` (Lvl ${level})`, base_gp: baseGp, economy_details: econ.steps, final_multiplier: phpRound(econ.multiplier, 3), final_gp: finalGp, pl: plOf(finalGp) };
            spellGp += finalGp;
        }
    }
    acc.totalGp += spellGp;

    let preparedGp = 0;
    if (lh(env, itemArr, 'spell_prepared_1')) {
        for (const entry of le(env, itemArr, 'spell_prepared_1')) {
            const spellId = entryZero(entry);
            if (!spellId) continue;
            const spellArr = requireDefinition(env.ctx, spellId);
            const n = itemField(env, spellArr, 'item_name');
            const level = phpJsonInt(lv(env, spellArr, 'spell_level') ?? 0);
            if (level <= 0) continue;
            const gp = phpIntRound(env.spells.getCostForLevel(level));
            bucket(acc, 'prepared_spells')[spellId] = { spell_name: typeof n === 'string' ? n : 'Unknown Spell', spell_level: level, spell_circle: lv(env, spellArr, 'circle_of_magic') ?? '', item_id: spellId, gp, pl: plOf(gp) };
            preparedGp += gp;
        }
    }
    acc.totalGp += preparedGp;

    // Vitality: computed_value($item_arr, 'vitality', 'v_max')
    const computed = isObj(itemArr.computed) ? itemArr.computed : null;
    const vit = computed && isObj(computed.vitality) ? computed.vitality : (isObj(itemArr.vitality) ? itemArr.vitality : null);
    let vMaxRaw: unknown = vit ? vit.v_max : null;
    if (Array.isArray(vMaxRaw)) vMaxRaw = vMaxRaw[0];
    const vMax = phpJsonInt(vMaxRaw ?? 0);
    if (vMax > 0) {
        const gpPerPoint = 100;
        const vitalityGp = phpIntRound(vMax * gpPerPoint);
        acc.breakdown.vitality = { v_max: vMax, gp_per_point: gpPerPoint, gp: vitalityGp, pl: plOf(vitalityGp) };
        acc.totalGp += vitalityGp;
    }
}

// HELPER 11: INVENTORY
export function calculateInventoryCosts(env: PassEnv, itemArr: Row, acc: PlAccumulator): void {
    let inventoryGp = 0.0;
    const sources: unknown[] = [];
    sources.push(...heldInventoryEntries(itemArr)); // an instance's own row bucket holds nothing (economy.ts)
    for (const key of ['inventory_equipped', 'inventory_unequipped']) sources.push(...le(env, itemArr, key));
    for (const entry of sources) {
        let itemGp = 0.0; let itemName: unknown = 'Unknown Item'; let itemId = '';
        if (isObj(entry) && itemField(env, entry, 'item_name') !== null) {
            const raw = env.ctx.thinScalar(entry as ItemFieldRow, 'cost_gp');
            itemGp = phpJsonFloat(raw ?? 0);
            itemName = itemField(env, entry, 'item_name');
            const id = itemField(env, entry, 'item_id');
            itemId = typeof id === 'string' ? id : '';
        } else {
            itemId = entryZero(entry);
            if (!itemId || !checkUuid(itemId)) continue;
            const invArr = requireDefinition(env.ctx, itemId);
            itemGp = phpJsonFloat(lv(env, invArr, 'cost_gp') ?? 0);
            const n = itemField(env, invArr, 'item_name');
            itemName = n ?? 'Unknown Item';
        }
        if (itemGp > 0) {
            bucket(acc, 'inventory')[itemId] = { item_name: itemName, gp: phpIntRound(itemGp), pl: plOf(itemGp) };
            inventoryGp += itemGp;
        }
    }
    acc.totalGp += phpIntRound(inventoryGp);
}

// HELPER 12: STATUS APPLICATION + TRIGGERS
/** PHP `$value == 0` (loose) for the trigger entry value. */
const looseZero = (v: unknown): boolean => v === 0 || v === null || v === false || (typeof v === 'string' && isPhpNumeric(v) && phpJsonFloat(v) === 0) || (typeof v === 'number' && v === 0);
export function calculateStatusApplicationAndTriggers(env: PassEnv, itemArr: Row, acc: PlAccumulator): void {
    const t = itemType(env, itemArr);
    const isAbilityItem = env.contract.abilityTypes.includes(t) || t === 'spell';
    const cats = plCatalogs(env.contract);
    let statusApplicationGp = 0;
    if (!isAbilityItem && t !== 'attack_ability') {
        const layers = ['status_effect_applied_to_target_after_contesting_roll_failed', 'status_effect_applied_to_target_on_hit', 'status_effect_applied_to_target_after_cast', 'status_effect_removed_from_target_after_cast',
            'status_effect_removed_from_target_after_contesting_roll_failed', 'status_effect_removed_from_target_on_hit', 'status_effect_applied_to_caster_after_contesting_roll_failed', 'status_effect_applied_to_caster_when_contestee_wins',
            'status_effect_applied_to_caster_on_hit', 'status_effect_applied_to_caster_after_cast', 'status_effect_removed_from_caster_after_cast', 'status_effect_removed_from_caster_after_contesting_roll_failed',
            'status_effect_removed_from_caster_on_hit', 'diseased_applied_to_target_after_contesting_roll_failed', 'user_choice_1_diseased_applied_to_target_after_contesting_roll_failed'];
        for (const layerType of layers) {
            const entries = le(env, itemArr, layerType);
            if (entries.length === 0) continue;
            for (const entry of entries) {
                let seUuid: unknown = typeof entry === 'string' ? entry : null;
                if (isObj(entry) || Array.isArray(entry)) { const z = (entry as Row)['0']; seUuid = (z !== undefined && z !== null) ? z : (itemField(env, entry as Row, 'item_id') ?? ''); }
                if (typeof seUuid !== 'string' || seUuid === '') continue;
                const statusData = cats.statusEffects.getById(seUuid);
                const varName = statusData?.variable_name ?? '';
                const statusType = statusData?.status_type ?? 'none';
                if (!varName) continue;
                const isCasterSelf = layerType.includes('_caster_');
                let gp = env.costs.getStatusApplicationCost(layerType, varName, isCasterSelf);
                if (gp === 0) continue;
                const isRemoval = layerType.includes('_removed_from_');
                const selfDrawback = isCasterSelf && ((!isRemoval && statusType === 'bane') || (isRemoval && statusType === 'boon'));
                if (selfDrawback) gp = -gp;
                const beneficial = !selfDrawback;
                const durationMult = env.costs.getDurationMultiplier(itemArr as ItemFieldRow, (r, n) => env.ctx.layerValue(r, n));
                gp = phpIntRound(gp * durationMult);
                sub(bucket(acc, 'status_effect_modifiers'), layerType)[varName] = { status: varName, type: layerType, status_type: statusType, beneficial, gp, pl: plOf(gp) };
                statusApplicationGp += gp;
            }
        }
        if (phpJsonTruthy(lv(env, itemArr, 'random_disease_applied_to_target_after_contesting_roll_failed'))) {
            const rdLayer = 'random_disease_applied_to_target_after_contesting_roll_failed';
            let rdGp = 2 * env.costs.getStatusApplicationCost(rdLayer, 'diseased', false);
            if (rdGp !== 0) {
                rdGp = phpIntRound(rdGp * env.costs.getDurationMultiplier(itemArr as ItemFieldRow, (r, n) => env.ctx.layerValue(r, n)));
                sub(bucket(acc, 'status_effect_modifiers'), rdLayer)['diseased'] = { status: 'diseased', type: rdLayer, status_type: 'bane', beneficial: true, gp: rdGp, pl: plOf(rdGp) };
                statusApplicationGp += rdGp;
            }
        }
    }
    acc.totalGp += statusApplicationGp;

    if (!isAbilityItem) {
        let triggerGp = 0;
        const triggerLayers = ['triggers_when_caster_makes_roll_type', 'triggers_when_target_makes_roll_type', 'triggers_when_caster_uses_item_type', 'triggers_when_target_uses_item_type', 'triggers_on_roll_type_against_the_caster',
            'triggers_on_roll_type_against_the_target', 'triggers_when_caster_gets_item_type_against', 'triggers_when_target_gets_item_type_against', 'triggers_when_caster_takes_any_damage', 'triggers_when_target_takes_any_damage',
            'triggers_when_caster_takes_damage_type', 'triggers_when_target_takes_damage_type', 'triggers_when_caster_gains_status_effect', 'triggers_when_target_gains_status_effect', 'triggers_when_caster_loses_status_effect',
            'triggers_when_target_loses_status_effect', 'triggers_if_roll_type_is_at_adv', 'triggers_if_roll_type_is_at_dis', 'triggers_on_next_roll_type', 'triggers_on_next_roll_type_against', 'triggers_on_miss', 'triggers_on_hit',
            'triggers_on_critical_hit', 'triggers_on_not_a_critical_hit', 'triggers_after_short_rest', 'triggers_after_long_rest', 'triggers_after_combat_begins', 'triggers_on_move', 'triggers_on_next_move',
            'triggers_when_target_stands_up_from_prone', 'triggers_when_target_moves', 'triggers_on_opportunity_attack', 'triggers_on_opportunity_attack_against', 'triggers_on_unlucky_roll', 'triggers_on_kill',
            'triggers_when_caster_turn_begins', 'triggers_when_caster_gains_luck', 'triggers_when_caster_spends_mana', 'triggers_on_self_zero_vitality', 'effect_is_reflected_back', 'triggers_if_ability_charge_is_used', 'triggers_if_ability_charge_is_not_used',
            'triggers_when_enemy_within_ft_reduced_to_zero_vitality'];
        for (const layerType of triggerLayers) {
            const entries = le(env, itemArr, layerType);
            if (entries.length === 0) continue;
            for (const entry of entries) {
                const value: unknown = (isObj(entry) || Array.isArray(entry)) ? ((entry as Row)['0'] ?? 1) : 1;
                if (looseZero(value)) continue;
                const multiplier = env.costs.getTriggerMultiplier(layerType);
                const gp = phpIntRound(500 * multiplier);
                if (gp === 0) continue;
                bucket(acc, 'trigger_modifiers')[layerType] = { trigger: layerType, multiplier: phpRound(multiplier, 2), gp, pl: plOf(gp) };
                triggerGp += gp;
            }
        }
        acc.totalGp += triggerGp;
    }
}

export { readItemNumericSlot, plCostLayerEntryUuid, phpJsonTruthy, phpJsonInt, phpJsonFloat, isPhpNumeric, phpRound, phpInt, phpIntRound, plOf, bucket, sub, entryZero };
